import React, { FC, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { apiUpdateUser } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { Button } from 'src/common/button';
import { useCurrentUser } from 'src/store/user';
import { apiSegmentTrackAnonymous } from 'src/utils/segment';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { Helmet } from 'react-helmet';
import { TextInput } from 'src/common/form/text-input';

const PURPOSE_OPTIONS = [
  'Fundraising docs',
  'Marketing presentations',
  'Product training',
  'Client portals',
  'Customer support docs',
  'Case studies',
  'Prospecting assets',
  'Sales followups',
  'Hiring resources',
  'Lead magnets',
  'Other'
];

const Checkbox: FC<{ option: string; checked: boolean; onChange: () => void }> = ({ option, checked, onChange }) => {
  return (
    <div 
      onClick={onChange}
      className='flex items-center p-4 rounded-lg border border-gray-200 hover:border-journey-primary-blue hover:bg-gray-100 transition-all duration-200 cursor-pointer'
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className='form-checkbox h-4 w-4 text-journey-primary-blue border-gray-300 rounded focus:ring-journey-primary-blue'
      />
      <label className='ml-3 text-bedrock-black flex text-sm cursor-pointer'>{option}</label>
    </div>
  );
};

export const PageOnboardingPurpose: FC = () => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);
  const setCurrentUser = useCurrentUser((state) => state.setCurrentUser);
  const { setErrorNotification } = useNotificationMessage();

  const toggleOption = (option: string) => {
    setSelectedOptions((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const nextStep = useCallback(() => {
    if (currentOrganization.is_onboarded) {
      window.location.replace('/');
    } else {
      history.push('./organization');
    }
  }, [currentOrganization.is_onboarded, history]);

  const onSubmit = async () => {
    try {
      setSubmitting(true);
      const purposes = selectedOptions.includes('Other') 
        ? [...selectedOptions.filter(opt => opt !== 'Other'), otherText] 
        : selectedOptions;

      const params: Record<string, any> = {
        purpose: purposes
      };

      const updatedUser = await apiUpdateUser(params);
      if (updatedUser) {
        setSubmitting(false);
        setCurrentUser(updatedUser);
        apiSegmentTrackAnonymous('Journey Purpose Selected', {
          purposes: purposes
        });
        nextStep();
      }
    } catch (error: any) {
      setSubmitting(false);
      setErrorNotification(error.message);
    }
  };

  const submitDisabled = 
    (selectedOptions.length === 0) || 
    (selectedOptions.includes('Other') && !otherText.trim()) || 
    submitting;

  return (
    <>
      <Helmet>
        <title>Journey | The Journey</title>
      </Helmet>
      <div className='flex flex-col items-center w-full max-w-[500px] mx-auto px-6 py-12'>
        <div className='text-4xl font-bold text-center mb-12 text-bedrock-black'>The Journey</div>
        <div className='w-full'>
          <div className='text-2xl font-semibold text-center mb-8 text-bedrock-black'>
            Where can Journey start making an impact? (Select all that apply)
          </div>
          <div className='flex flex-col gap-3'>
            {PURPOSE_OPTIONS.map((option) => (
              <Checkbox
                key={option}
                option={option}
                checked={selectedOptions.includes(option)}
                onChange={() => toggleOption(option)}
              />
            ))}
            {selectedOptions.includes('Other') && (
              <TextInput
                id='purpose_other'
                placeholder='Please specify other purpose'
                onChange={(e: any) => setOtherText(e.target.value)}
                autoFocus
                inputProps={{ 
                  value: otherText,
                  className: 'w-full p-3 border border-gray-200 rounded-lg focus:border-journey-primary-blue focus:ring-1 focus:ring-journey-primary-blue transition-all duration-200'
                }}
              />
            )}
          </div>
          <div className='flex mt-8'>
            <Button
              type='submit'
              variant='primary'
              size='regular'
              className='w-full py-4 text-lg font-semibold rounded-lg'
              onClick={onSubmit}
              disabled={submitDisabled}
              label='Next'
            />
          </div>
        </div>
      </div>
    </>
  );
};